import React, { createContext, useState, useContext, useEffect } from 'react';
import API from '../api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on page reload
  useEffect(() => {
    const storedUser = localStorage.getItem('poshe_user') || sessionStorage.getItem('poshe_user');
    const token = localStorage.getItem('poshe_token') || sessionStorage.getItem('poshe_token');
    
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      API.get('/api/auth/me')
        .then((res) => {
          setUser(res.data.user);
          const storage = localStorage.getItem('poshe_token') ? localStorage : sessionStorage;
          storage.setItem('poshe_user', JSON.stringify(res.data.user));
        })
        .catch(() => {
          localStorage.removeItem('poshe_token');
          localStorage.removeItem('poshe_user');
          sessionStorage.removeItem('poshe_token');
          sessionStorage.removeItem('poshe_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const { data } = await API.post('/api/auth/login', { email, password });
      if (data.success) {
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('poshe_token', data.token);
        storage.setItem('poshe_user', JSON.stringify(data.user));
        setUser(data.user);
        toast.success(`Welcome back, ${data.user.name}!`);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (name, email, password, confirmPassword) => {
    try {
      const { data } = await API.post('/api/auth/register', { name, email, password, confirmPassword: confirmPassword || password });
      if (data.success) {
        localStorage.setItem('poshe_token', data.token);
        localStorage.setItem('poshe_user', JSON.stringify(data.user));
        setUser(data.user);
        toast.success('Account created successfully! Welcome to Poshe Styles ✦');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('poshe_token');
    localStorage.removeItem('poshe_user');
    sessionStorage.removeItem('poshe_token');
    sessionStorage.removeItem('poshe_user');
    setUser(null);
    toast.info('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
